import React, { type ComponentType, type Ref } from 'react';
import { cn } from '@/lib/utils';
import clsx from 'clsx';
import { Select, SelectContent, SelectTrigger, SelectValue } from '@/components/ui/select';
import IconArrowDown from '@/assets/icons/icon-arrow-down-01.svg?react';

interface ZoomSelectProps {
  className?: string;
  classNameContent?: string;
  classNameValue?: string;
  classNameTrigger?: string;
  placeholder?: string;
  text?: string;
  size?: 'small' | 'large' | 'full' | 'none';
  children: React.ReactNode;
  icon?: ComponentType<React.SVGProps<SVGSVGElement>> | null;
  value?: string;
  customValues?: boolean;
  onValueChange?: (value: string) => void;
  triggerRef?: Ref<HTMLButtonElement>;
  // Controls which side the dropdown opens; default to 'top' so it doesn't get hidden at the bottom of the screen
  contentSide?: 'top' | 'bottom';
  'data-qa-selector'?: string;
}

export const ZoomSelect: React.FC<ZoomSelectProps> = ({
  className = '',
  classNameContent = '',
  classNameValue = '',
  classNameTrigger = '',
  text = 'Text',
  size = 'small',
  children,
  placeholder = 'Theme',
  icon: Icon,
  value,
  customValues = false,
  onValueChange,
  triggerRef,
  contentSide = 'top',
  'data-qa-selector': dataQaSelector,
}) => {
  const handleValueChange = (newValue: string) => {
    onValueChange?.(newValue);
  };

  return (
    <div className={cn('flex items-center justify-between', className)}>
      {text && <p className="text-button-sm font-medium text-neutral-500">{text}</p>}

      <Select value={value} onValueChange={handleValueChange}>
        <SelectTrigger
          className={clsx(
            'group relative w-full overflow-hidden pr-8',
            {
              'max-w-[124px]': size === 'small',
              'max-w-[160px]': size === 'large',
              'max-w-[216px]': size === 'full',
            },
            classNameTrigger,
          )}
          data-qa-selector={dataQaSelector}
        >
          {customValues === true && (
            <div className="absolute top-2 left-0.5 z-10 flex h-4 w-8 items-center rounded-full bg-neutral-50 p-2 text-neutral-900">
              {value}
            </div>
          )}
          {Icon && <Icon className="h-4.5 w-4.5 text-neutral-500" />}
          <SelectValue className={classNameValue} ref={triggerRef} placeholder={placeholder} />
          <IconArrowDown className="pointer-events-none absolute top-1/2 right-2 h-4 w-4 -translate-y-1/2 text-neutral-500 transition-transform duration-200 group-aria-expanded:rotate-180" />
        </SelectTrigger>
        <SelectContent side={contentSide} className={clsx(classNameContent)}>
          {children}
        </SelectContent>
      </Select>
    </div>
  );
};
